"use client"

import { useShallow } from "zustand/react/shallow"

import type { VisualizerSettings } from "@/types"

import { useStore } from "./index"
import {
  selectAudioAssetId,
  selectAudioDuration,
  selectAudioFile,
  selectAudioMeta,
  selectAudioUrl,
  selectBeatGrid,
  selectBgVideoAssetId,
  selectBgVideoFile,
  selectBgVideoUrl,
  selectCustomFontAssetId,
  selectGridError,
  selectGridStatus,
  selectIsPlaying,
  selectLastSavedAt,
  selectLyrics,
  selectLyricsHistory,
  selectProjectId,
  selectProjectName,
  selectSaveStatus,
  selectScreen,
  selectSettings,
  selectThumbnailDataUrl,
} from "./selectors"

export const useScreen = () => useStore(selectScreen)
export const useSetScreen = () => useStore((s) => s.setScreen)

export const useLyrics = () => useStore(selectLyrics)
export const useLyricsActions = () =>
  useStore(
    useShallow((s) => ({
      setLyrics: s.setLyrics,
      updateLyric: s.updateLyric,
      addLyric: s.addLyric,
      removeLyric: s.removeLyric,
      stampTime: s.stampTime,
      reorderLyrics: s.reorderLyrics,
      toggleLock: s.toggleLock,
      undo: s.undo,
      redo: s.redo,
    })),
  )
export const useLyricsHistory = () => useStore(useShallow(selectLyricsHistory))

export const useSettings = () => useStore(selectSettings)
export const useSetting = <K extends keyof VisualizerSettings>(key: K) =>
  useStore((s) => s.settings[key])
export const useSetSetting = () => useStore((s) => s.setSetting)

export const useRecentColors = () => useStore((s) => s.recentColors)
export const usePushRecentColor = () => useStore((s) => s.pushRecentColor)
export const useApplyTemplate = () => useStore((s) => s.applyTemplate)
export const useUndoSettings = () => useStore((s) => s.undoSettings)
export const useRedoSettings = () => useStore((s) => s.redoSettings)
export const useSettingsHistory = () =>
  useStore(
    useShallow((s) => ({
      canUndo: s.settingsHistoryIdx >= 0,
      canRedo: s.settingsHistoryIdx < s.settingsHistory.length - 1,
    })),
  )

export const useIsPlaying = () => useStore(selectIsPlaying)
export const useSetPlaying = () => useStore((s) => s.setPlaying)

export const useAudioFile = () => useStore(selectAudioFile)
export const useAudioUrl = () => useStore(selectAudioUrl)
export const useAudioDuration = () => useStore(selectAudioDuration)
export const useSetAudio = () => useStore((s) => s.setAudio)

export const useBgVideoFile = () => useStore(selectBgVideoFile)
export const useBgVideoUrl = () => useStore(selectBgVideoUrl)
export const useSetBgVideo = () => useStore((s) => s.setBgVideo)
export const useClearBgVideo = () => useStore((s) => s.clearBgVideo)

export const useProjectId = () => useStore(selectProjectId)
export const useProjectName = () => useStore(selectProjectName)
export const useSaveStatus = () => useStore(selectSaveStatus)
export const useLastSavedAt = () => useStore(selectLastSavedAt)
export const useSetProject = () => useStore((s) => s.setProject)
export const useSetProjectName = () => useStore((s) => s.setProjectName)
export const useSetSaveStatus = () => useStore((s) => s.setSaveStatus)
export const useMarkSaved = () => useStore((s) => s.markSaved)
export const useClearProject = () => useStore((s) => s.clearProject)
export const useAudioAssetId = () => useStore(selectAudioAssetId)
export const useAudioMeta = () => useStore(selectAudioMeta)
export const useBgVideoAssetId = () => useStore(selectBgVideoAssetId)
export const useCustomFontAssetId = () => useStore(selectCustomFontAssetId)
export const useThumbnailDataUrl = () => useStore(selectThumbnailDataUrl)
export const useSetProjectAssets = () => useStore((s) => s.setProjectAssets)

export const useBeatGrid = () => useStore(selectBeatGrid)
export const useGridStatus = () => useStore(selectGridStatus)
export const useGridError = () => useStore(selectGridError)
export const useSetBeatGrid = () => useStore((s) => s.setBeatGrid)
export const useClearBeatGrid = () => useStore((s) => s.clearBeatGrid)
export const useSetGridStatus = () => useStore((s) => s.setGridStatus)
